"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser } from "@/lib/authSession";
import {
  clearUserSessionOnLogout,
  getAuthUserFromCookie,
  setAuthUserCookie,
} from "@/lib/authSession";

type AuthContextType = {
  user: AuthUser | null;
  isLoggedIn: boolean;
  isReady: boolean;
  login: (user: AuthUser) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setUser(getAuthUserFromCookie());
      setIsReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const login = useCallback((u: AuthUser) => {
    setAuthUserCookie(u);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    clearUserSessionOnLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoggedIn: !!user,
      isReady,
      login,
      logout,
    }),
    [user, isReady, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
